/**
 * Best-effort lookup of the visitor's country via the same-origin
 * /api/geo Cloudflare Pages Function (see functions/api/geo.ts, which reads
 * Cloudflare's own `request.cf.country` - no third-party geolocation
 * service, no API key/signup, no CORS setup needed since it's same-origin).
 * Used ONLY to pick a sensible starting UI language on a visitor's very
 * first visit (see LanguageContext) - never for anything else, and only
 * runs once (the result is cached in localStorage from then on).
 *
 * Returns null (never throws) on any failure - route not found (e.g.
 * running plain `vite dev` locally instead of `wrangler pages dev`, or
 * hosting somewhere other than Cloudflare Pages), network error, or a slow
 * response past the timeout - so callers always have a safe fallback path
 * (browser language, then English). This is deliberately best-effort and
 * non-blocking: a visitor should never wait long, or get stuck, just
 * because this one guess is slow or unavailable.
 */
export async function detectCountryFromIp(timeoutMs = 1500): Promise<string | null> {
  if (typeof fetch !== "function") return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch("/api/geo", { signal: controller.signal });
    if (!response.ok) return null;
    const data = (await response.json()) as { country?: string | null };
    return data.country ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
